import React, { useEffect, useState, useCallback } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { GridApi, ColDef, GridReadyEvent } from 'ag-grid-community'
import { AGTableColumns } from './AGTableColumns'
import { useGridConfigurationStore } from '@/stores/grid-configuration.store'

type AgGridReactProps = React.ComponentProps<typeof AgGridReact>

interface AGTableWrapperProps {
  collection: string
  children: React.ReactElement<AgGridReactProps>
}

export const AGTableWrapper: React.FC<AGTableWrapperProps> = ({ collection, children }) => {
  const [gridApi, setGridApi] = useState<GridApi | null>(null)
  const [gridReady, setGridReady] = useState(false)
  const { getCollectionState, fetchGridConfiguration } = useGridConfigurationStore()

  useEffect(() => {
    fetchGridConfiguration()
  }, [fetchGridConfiguration])

  useEffect(() => {
    if (gridApi && gridReady) {
      const collectionState = getCollectionState(collection)
      if (collectionState) {
        const columns = gridApi.getColumnDefs() as ColDef[]
        const result = columns?.map(column => {
          const found = collectionState.state[collectionState.selected]?.find(
            x => x.colId === column.colId
          )
          if (found) {
            // Remove deprecated suppressMenu property if present
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { suppressMenu: _suppressMenu, ...foundWithoutSuppressMenu } = found as ColDef & {
              suppressMenu?: boolean
            }
            return {
              ...column,
              ...foundWithoutSuppressMenu,
            }
          }
          return column
        })
        if (result) {
          // Also remove suppressMenu from all column definitions
          const cleanedResult = result.map(col => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { suppressMenu: _suppressMenu, ...colWithoutSuppressMenu } = col as ColDef & {
              suppressMenu?: boolean
            }
            return colWithoutSuppressMenu
          })
          gridApi.updateGridOptions({ columnDefs: [] })
          gridApi.updateGridOptions({ columnDefs: cleanedResult })
        }
      }
    }
  }, [gridApi, gridReady, collection, getCollectionState])

  const handleGridReady = useCallback(
    (params: GridReadyEvent) => {
      setGridApi(params.api)
      setGridReady(true)
      const originalOnGridReady = children.props.onGridReady
      if (originalOnGridReady) {
        originalOnGridReady(params)
      }
    },
    [children]
  )

  const clonedChildren = React.cloneElement(children, {
    onGridReady: handleGridReady,
  })

  return (
    <div className="w-full h-full overflow-hidden relative flex flex-row">
      <div className="flex-1 min-w-0 overflow-hidden" style={{ height: '100%' }}>
        {clonedChildren}
      </div>
      {collection && gridReady && gridApi && (
        <AGTableColumns collection={collection} gridApi={gridApi} />
      )}
    </div>
  )
}
